import { useMemo } from "react";
import DataTable from "react-data-table-component";
import { createColumns } from "../../helpers";
import { Link, useParams } from "react-router-dom";
import { DATATABLE_CUSTOM_STYLES } from "../../constants/datatableStyles";
import RowLoader from "../UI/RowLoader";
import EditIcon from "../Svgs/EditIcon";
import DeleteIcon from "../Svgs/DeleteIcon";
import CustomPagination from "../CustomPagination";
import Swal from "sweetalert2";
import { deleteLessonInCourseApi, getLessonsInCourse } from "../../apis";
import EyeDetailsIcon from "../Svgs/EyeDetailsIcon";
import ViewLessonModal from "./ViewLessonModal";

const ListLessonsInCourse = ({
  title,
  data,
  paginator,
  isLoading = false,
  handlePageChange,
  isError = false,
  setCourseDetails,
  className = "tw-container tw-mx-auto tw-p-5 tw-bg-white tw-rounded-[10px] tw-shadow-md tw-relative",
}) => {
  const { courseId } = useParams();

  const handleDeleteLesson = (lessonId) => {
    if (!lessonId) return;

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: () => {
        return deleteLessonInCourseApi(courseId, lessonId)
          .then((response) => {
            return response.data;
          })
          .catch((error) => {
            Swal.showValidationMessage(
              error.response.data.message ?? "Failed to delete the lesson"
            );
            console.error("Error deleting channel:", error);
          });
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire("Deleted!", "The lesson has been deleted.", "success");

        // refetch data after deleting the lesson
        getLessonsInCourse(courseId)
          .then((response) => {
            setCourseDetails(response.data);
          })
          .catch((error) => {
            console.error("Error fetching list lessons", error);
          });
      }
    });
  };
  const hiddenColumns = ["course_id", "id"];
  const customColumns = [
    {
      name: "Edit",
      selector: (row) => row.name + " - custom",
      ui: (row) => (
        <div className="tw-flex tw-gap-1">
          <Link to={`./edit-lesson/${row.id}`}>
            <EditIcon width="22px" height="30px" />
          </Link>
        </div>
      ),
    },
    {
      name: "Delete",
      selector: (row) => row.name + " - custom",
      ui: (row) => (
        <button
          className="!tw-m-0 !tw-p-0"
          onClick={() => handleDeleteLesson(row.id)}
        >
          <DeleteIcon width="17px" />
        </button>
      ),
    },
  ];

  const columns = useMemo(() => {
    return createColumns(data, customColumns, hiddenColumns, true).map(
      (col) => {
        if (col.name === "Lesson Link") {
          return {
            ...col,
            selector: (row) => (
              <div className="tw-justify-center tw-flex tw-cursor-pointer">
                <ViewLessonModal videoUrl={row.lesson_link} />
              </div>
            ),
            sortable: false,
          };
        }

        return {
          ...col,
          sortable: col.name === "Num",
          width: ["Num", "Details", "Edit", "Delete"].includes(col.name)
            ? "80px"
            : "1fr",
        };
      }
    );
  }, [data]);

  return (
    <section className={className}>
      <div className="tw-pb-2 tw-flex max-md:tw-flex-col max-md:tw-gap-4 justify-content-between tw-items-center tw-mb-4 tw-border-b">
        <h3 className="tw-text-xl tw-font-semibold tw-text-red-600 tw-p-0 !tw-m-0">
          {title}
        </h3>
      </div>
      {isError ? (
        <p className="tw-text-center tw-text-red-500">{isError}</p>
      ) : (
        <DataTable
          columns={columns}
          data={data}
          customStyles={DATATABLE_CUSTOM_STYLES}
          progressPending={isLoading}
          progressComponent={<RowLoader />}
        />
      )}
      <div className="flex justify-center mt-4">
        <CustomPagination
          totalPages={paginator?.last_page}
          currentPage={paginator?.current_page || 1}
          onPageChange={handlePageChange}
        />
      </div>
    </section>
  );
};

export default ListLessonsInCourse;
